import React, { ChangeEvent, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { validateFieldWithSchema } from '../utils/util';
import { IsFieldValid } from '../shared/interfaces';
import processImg from '../assets/process.gif';
import logoImg from '../assets/logo.png';
import apiService from '../services/ApiService';
import NotificationBanner from './common/NotificationBanner';
import { applyTheme, rememberedTheme } from '../theme/applyTheme';
import { UserIcon, LockClosedIcon, EyeIcon, EyeSlashIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

interface LoginFormData {
  userName: string;
  password: string;
}

export const Login: React.FC = () => {
  const fieldNames = [
    "userName",
    "password",
  ] as const;

  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    applyTheme(rememberedTheme() ?? 'default');
  }, []);
  type Fields = Record<(typeof fieldNames)[number], boolean>;
  type ValidFields = Record<(typeof fieldNames)[number], IsFieldValid>;
  const [isClicked, setIsClicked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [forceLogoutExisting, setForceLogoutExisting] = useState(false);
  const [showForceLogoutOption, setShowForceLogoutOption] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [authenticatorRequired, setAuthenticatorRequired] = useState(false);
  const [authenticatorCode, setAuthenticatorCode] = useState('');
  const [formData, setFormData] = useState<LoginFormData>({
    userName: '',
    password: '',
  });

  const [dirtyFields, setDirtyFields] = useState<Fields>(
    fieldNames.reduce((acc, key) => ({ ...acc, [key]: false }), {} as Fields)
  );

  const [isFieldValid, setIsFieldValid] = useState<ValidFields>(
    fieldNames.reduce((acc, key) => ({ ...acc, [key]: { isValid: false, message: "" } }), {} as ValidFields)
  );

  const loginForm = z.object({
    userName: z.string({ required_error: "Username is required" }).min(3, "Username should be atleast 3 char").max(20),
    password: z
      .string({ required_error: "Password is required" })
      .min(8, "Password must be at least 8 characters")
      .max(16, "Password must be at most 16 characters")
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/,
        "Password must contain at least 1 lowercase, 1 uppercase, 1 number, and 1 special character"
      ),
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setDirtyFields((prev: Fields) => ({
      ...prev,
      [name]: true
    }))

    setIsFieldValid((prev: ValidFields) => {
      const { isValid, message } = validateFieldWithSchema(
        name,
        value,
        formData,
        loginForm
      );

      return {
        ...prev,
        [name]: { isValid, message },
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    setIsClicked(true);
    setLoginError(null);
    e.preventDefault();
    // Add authentication logic here
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const username = data.get('userName');
    const password = data.get('password');
    try {
      apiService.post('/Auth/login', {
        username,
        password,
        forceLogoutExisting: forceLogoutExisting,
        ...(authenticatorRequired ? { authenticatorCode } : {}),
      }).then(() => {
        setIsClicked(false);
        setShowForceLogoutOption(false);
        setForceLogoutExisting(false);
        setAuthenticatorRequired(false);
        setAuthenticatorCode('');
        void login().then(() => {
          navigate('/');
          window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: 'Login successful', type: 'success' } }));
        });
      }).catch((err: any) => {
        setIsClicked(false);
        const message = err?.message ?? err?.Message ?? (typeof err === 'string' ? err : '');
        const msg = typeof message === 'string' ? message : '';
        const needsAuthenticator =
          err?.code === 'AUTHENTICATOR_REQUIRED' ||
          err?.code === 'AUTHENTICATOR_INVALID' ||
          err?.code === 'AUTHENTICATOR_LOCKED';
        const isTenantDeactivated = /tenant is deactivated|user account is deactivated/i.test(msg);
        const isAlreadyLoggedIn =
          err?.code === 'ALREADY_LOGGED_IN' ||
          err?.errorNo === 1001 ||
          /already logged in|logged in elsewhere|other (device|browser|tab|session)|another (device|browser|tab|session)/i.test(msg);
        if (needsAuthenticator) {
          setAuthenticatorRequired(true);
          setShowForceLogoutOption(false);
          setLoginError(msg || 'Enter the 6-digit code from your authenticator app.');
          window.dispatchEvent(new CustomEvent('show-toast', {
            detail: { message: msg || 'Enter the code from your authenticator app.', type: 'info' },
          }));
        } else if (isTenantDeactivated) {
          setShowForceLogoutOption(false);
          setForceLogoutExisting(false);
          setLoginError(msg || 'Tenant is deactivated. Please contact administrator.');
          window.dispatchEvent(new CustomEvent('show-toast', {
            detail: { message: msg || 'Tenant is deactivated. Please contact administrator.', type: 'error' },
          }));
        } else if (isAlreadyLoggedIn) {
          setShowForceLogoutOption(true);
          window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: message || 'You are already logged in elsewhere.', type: 'info' } }));
        } else {
          setShowForceLogoutOption(false);
          setForceLogoutExisting(false);
          setLoginError(null);
          window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: message || 'Invalid Username or Password. Try Again.', type: 'info' } }));
        }
      });
    } catch (error) {
      setIsClicked(false);
      console.error("Login failed:", error);
      window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: 'Login failed. Please try again.', type: 'error' } }));
    }
  };

  return (
    <div className="login-screen flex h-screen w-full flex-col">
      <NotificationBanner commonOnly />
      <div className="flex min-h-0 flex-1 w-full">
      {/* Left Column - Promotional Panel */}
      <div className="hidden lg:flex lg:w-[45%] bg-gradient-to-b from-blue-800 via-blue-700 to-blue-900 relative overflow-hidden dark:from-slate-800 dark:via-slate-700 dark:to-slate-900">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/20 rounded-full blur-3xl dark:bg-slate-500/20"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl dark:bg-slate-600/20"></div>
        
        <div className="relative z-10 flex flex-col justify-between p-8 lg:p-12 text-white">
          {/* Logo and Branding */}
          <div>
            <div className="flex items-center gap-3 mb-8">
              <img src={logoImg} alt="Tiger Software" className="h-10 w-10 rounded-lg object-contain" />
              <span className="text-2xl font-bold">Tiger Software</span>
            </div>
            
            <h1 className="text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              Streamline Your Accounting Operations
            </h1>
            <p className="text-lg text-blue-100 leading-relaxed max-w-md dark:text-slate-200">
              Manage your financial transactions, track party accounts, generate reports, and maintain accurate ledgers with our comprehensive accounting solution.
            </p>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-3 gap-6 mt-8">
            <div>
              <div className="text-3xl font-bold mb-1">100%</div>
              <div className="text-sm text-blue-200 dark:text-slate-300">Accuracy</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-1">24/7</div>
              <div className="text-sm text-blue-200 dark:text-slate-300">Access</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-1">Secure</div>
              <div className="text-sm text-blue-200 dark:text-slate-300">Platform</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column - Login Form */}
      <div className="flex flex-1 items-center justify-center bg-transparent px-4 sm:px-6 lg:px-12">
        <div className="w-full max-w-md">
          {/* Welcome Section */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome back</h2>
            <p className="text-gray-600">Sign in to your accounting account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {loginError && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                {loginError}
              </div>
            )}
            {/* Username Field */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <UserIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="username"
                  name="userName"
                  required
                  onBlur={handleChange}
                  className={`w-full pl-10 pr-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    dirtyFields.userName && !isFieldValid.userName.isValid 
                      ? 'border-red-500 bg-red-50' 
                      : 'border-gray-300 bg-white hover:border-gray-400'
                  }`}
                  placeholder="Enter your username"
                />
              </div>
              {dirtyFields.userName && !isFieldValid.userName.isValid && (
                <p className="text-red-500 text-xs mt-1 ml-1">{isFieldValid.userName.message}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LockClosedIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  maxLength={16}
                  required
                  onBlur={handleChange}
                  className={`w-full pl-10 pr-10 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    dirtyFields.password && !isFieldValid.password.isValid 
                      ? 'border-red-500 bg-red-50' 
                      : 'border-gray-300 bg-white hover:border-gray-400'
                  }`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {dirtyFields.password && !isFieldValid.password.isValid && (
                <p className="text-red-500 text-xs mt-1 ml-1">{isFieldValid.password.message}</p>
              )}
            </div>

            {authenticatorRequired && (
              <div>
                <label htmlFor="authenticatorCode" className="block text-sm font-medium text-gray-700 mb-2">
                  Authenticator code
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <ShieldCheckIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="authenticatorCode"
                    name="authenticatorCode"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    required
                    autoFocus
                    value={authenticatorCode}
                    onChange={(e) => setAuthenticatorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-300 bg-white rounded-lg tracking-[0.4em] font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="000000"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1 ml-1">
                  Enter the 6-digit code shown in the authenticator app. Each code works once, then a new one appears.
                </p>
              </div>
            )}

            {/* Force logout other session - only when backend says user is logged in on another device/browser/tab */}
            {showForceLogoutOption && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={forceLogoutExisting}
                    onChange={(e) => setForceLogoutExisting(e.target.checked)}
                    className="h-4 w-4 rounded border-amber-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>Log out other session and sign in here</span>
                </label>
              </div>
            )}

            {/* Sign In Button */}
            <button
              type="submit"
              className="w-full flex justify-center items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-lg font-semibold text-sm shadow-md hover:shadow-lg hover:from-blue-700 hover:to-blue-800 transform hover:scale-[1.01] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              disabled={isClicked}
            >
              {isClicked ? (
                <>
                  <img src={processImg} className="w-4 h-4" alt="Logging in..." />
                  <span>Signing in...</span>
                </>
              ) : (
                <span>Sign In</span>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Secure login with encrypted credentials
            </p>
            <p className="text-sm text-gray-600 mt-2 font-medium">
              Contact: <a href="tel:+15812568748" className="text-blue-600 hover:text-blue-700">+1 (581) 256-8748</a>
            </p>
            <p className="text-xs text-gray-400 mt-1">
              © All rights reserved for Tiger Software Private Ltd.
            </p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};